import { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import {
  Award,
  Flame,
  Keyboard,
  Layers3,
  MousePointerClick,
  Sparkles,
  TimerReset,
  Trophy,
  X,
  Zap,
} from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../hooks/useLanguage';
import {
  buildAchievementViewModel,
  type AchievementIcon,
  type AchievementUnlockRecord,
  type AchievementViewModel,
} from '../lib/achievements';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from './ui/sheet';
import { useIsMobile } from './ui/use-mobile';

interface AchievementCelebrationCenterProps {
  unlocks: AchievementUnlockRecord[];
  onHandled: () => void;
}

const toneMap: Record<AchievementViewModel['category'], { shell: string; badge: string; icon: string }> = {
  starter: {
    shell: 'border-sky-200/70 bg-gradient-to-br from-sky-50 via-background to-cyan-50 dark:border-sky-900/60 dark:from-sky-950/50 dark:to-cyan-950/30',
    badge: 'border-sky-300/70 bg-sky-100 text-sky-700 dark:border-sky-800 dark:bg-sky-950 dark:text-sky-300',
    icon: 'text-sky-600 dark:text-sky-300',
  },
  consistency: {
    shell: 'border-orange-200/70 bg-gradient-to-br from-orange-50 via-background to-amber-50 dark:border-orange-900/60 dark:from-orange-950/40 dark:to-amber-950/20',
    badge: 'border-orange-300/70 bg-orange-100 text-orange-700 dark:border-orange-800 dark:bg-orange-950 dark:text-orange-300',
    icon: 'text-orange-600 dark:text-orange-300',
  },
  progress: {
    shell: 'border-emerald-200/70 bg-gradient-to-br from-emerald-50 via-background to-teal-50 dark:border-emerald-900/60 dark:from-emerald-950/40 dark:to-teal-950/20',
    badge: 'border-emerald-300/70 bg-emerald-100 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-300',
    icon: 'text-emerald-600 dark:text-emerald-300',
  },
  volume: {
    shell: 'border-violet-200/70 bg-gradient-to-br from-violet-50 via-background to-fuchsia-50 dark:border-violet-900/60 dark:from-violet-950/40 dark:to-fuchsia-950/20',
    badge: 'border-violet-300/70 bg-violet-100 text-violet-700 dark:border-violet-800 dark:bg-violet-950 dark:text-violet-300',
    icon: 'text-violet-600 dark:text-violet-300',
  },
  specialization: {
    shell: 'border-rose-200/70 bg-gradient-to-br from-rose-50 via-background to-pink-50 dark:border-rose-900/60 dark:from-rose-950/40 dark:to-pink-950/20',
    badge: 'border-rose-300/70 bg-rose-100 text-rose-700 dark:border-rose-800 dark:bg-rose-950 dark:text-rose-300',
    icon: 'text-rose-600 dark:text-rose-300',
  },
};

function getIcon(icon: AchievementIcon) {
  if (icon === 'flame') return Flame;
  if (icon === 'zap') return Zap;
  if (icon === 'trophy') return Trophy;
  if (icon === 'keyboard') return Keyboard;
  if (icon === 'cards') return Layers3;
  if (icon === 'pointer') return MousePointerClick;
  if (icon === 'timer') return TimerReset;
  return Sparkles;
}

function AchievementRewards({ rewardLines }: { rewardLines: AchievementViewModel['rewardLines'] }) {
  if (rewardLines.length === 0) {
    return null;
  }

  return (
    <div className="grid gap-2 sm:grid-cols-2">
      {rewardLines.map((item) => (
        <div key={`${item.label}-${item.value}`} className="rounded-xl border border-border/50 bg-background/70 px-3 py-2">
          <div className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">{item.label}</div>
          <div className="mt-1 text-sm font-semibold text-foreground">{item.value}</div>
        </div>
      ))}
    </div>
  );
}

function AchievementPanel({
  achievement,
  compact = false,
  showCopy = true,
}: {
  achievement: AchievementViewModel;
  compact?: boolean;
  showCopy?: boolean;
}) {
  const tone = toneMap[achievement.category];
  const Icon = getIcon(achievement.icon);

  return (
    <div className={`relative overflow-hidden rounded-2xl border ${tone.shell} ${compact ? 'p-4' : 'p-5 sm:p-6'}`}>
      <div className="absolute inset-x-6 top-0 h-20 rounded-full bg-white/50 blur-3xl dark:bg-white/5" />
      <div className="relative space-y-4">
        <div className="flex items-start justify-between gap-4">
          {showCopy ? (
            <div className="space-y-2">
              <Badge variant="outline" className={tone.badge}>
                Achievement unlocked
              </Badge>
              <div>
                <h3 className={`${compact ? 'text-base' : 'text-2xl'} font-semibold tracking-tight`}>{achievement.title}</h3>
                <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{achievement.description}</p>
              </div>
            </div>
          ) : (
            <Badge variant="outline" className={tone.badge}>
              Achievement unlocked
            </Badge>
          )}
          <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-white/50 bg-background/80 shadow-sm ${tone.icon}`}>
            <Icon className="h-6 w-6" />
          </div>
        </div>

        <AchievementRewards rewardLines={achievement.rewardLines} />
      </div>
    </div>
  );
}

export function AchievementCelebrationCenter({ unlocks, onHandled }: AchievementCelebrationCenterProps) {
  const { currentLang, t } = useLanguage();
  const isMobile = useIsMobile();
  const [activeBatchKey, setActiveBatchKey] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [visibleCards, setVisibleCards] = useState<AchievementViewModel[]>([]);

  const viewModels = useMemo(
    () => unlocks.map((unlock) => buildAchievementViewModel(unlock, currentLang)),
    [currentLang, unlocks],
  );

  const batchKey = useMemo(
    () => unlocks.map((unlock) => unlock.unlockKey).join('|'),
    [unlocks],
  );

  const featuredModal = useMemo(
    () => viewModels.find((item) => item.celebration === 'modal') ?? null,
    [viewModels],
  );

  const extraUnlocks = useMemo(
    () => (featuredModal ? viewModels.filter((item) => item.unlockKey !== featuredModal.unlockKey).slice(0, 3) : []),
    [featuredModal, viewModels],
  );

  useEffect(() => {
    if (!unlocks.length || batchKey === activeBatchKey) {
      return;
    }

    setActiveBatchKey(batchKey);
    setVisibleCards([]);

    const modalItems = viewModels.filter((item) => item.celebration === 'modal');
    if (modalItems.length > 0) {
      setModalOpen(true);
      return;
    }

    const cardItems = viewModels.filter((item) => item.celebration === 'card');
    if (cardItems.length > 0) {
      setVisibleCards(cardItems.slice(0, 2));
      return;
    }

    const toastItems = viewModels.filter((item) => item.celebration === 'toast');
    if (toastItems.length > 0) {
      toastItems.slice(0, 2).forEach((item) => {
        toast.success(item.title, { description: item.description, duration: 4000 });
      });

      if (toastItems.length > 2) {
        toast.success(currentLang === 'uk' ? 'Ще досягнення відкрито' : 'More achievements unlocked', {
          description: currentLang === 'uk'
            ? `Ще ${toastItems.length - 2} досягнення готові.`
            : `${toastItems.length - 2} more milestones are ready.`,
          duration: 4000,
        });
      }
    }

    const finishTimer = window.setTimeout(() => {
      onHandled();
    }, 150);

    return () => {
      window.clearTimeout(finishTimer);
    };
  }, [activeBatchKey, batchKey, currentLang, onHandled, unlocks, viewModels]);

  useEffect(() => {
    if (visibleCards.length === 0) {
      return;
    }

    const timerId = window.setTimeout(() => {
      setVisibleCards([]);
      onHandled();
    }, 5200);

    return () => {
      window.clearTimeout(timerId);
    };
  }, [onHandled, visibleCards]);

  const handleCloseModal = (nextOpen: boolean) => {
    setModalOpen(nextOpen);
    if (!nextOpen) {
      onHandled();
    }
  };

  const modalBody = featuredModal ? (
    <div className="space-y-4">
      <AchievementPanel achievement={featuredModal} showCopy={false} />
      {extraUnlocks.length > 0 ? (
        <div className="space-y-2">
          <div className="text-sm font-medium text-muted-foreground">
            {currentLang === 'uk' ? 'Ще відкрито' : 'Also unlocked'}
          </div>
          <div className="grid gap-2">
            {extraUnlocks.map((item) => {
              const Icon = getIcon(item.icon);
              return (
                <div key={item.unlockKey} className="flex items-center gap-3 rounded-xl border border-border/50 bg-muted/20 px-3 py-2.5">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-background shadow-sm">
                    <Icon className={`h-4 w-4 ${toneMap[item.category].icon}`} />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-foreground">{item.title}</div>
                    <div className="text-xs text-muted-foreground">{item.description}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  ) : null;

  return (
    <>
      {featuredModal ? (
        isMobile ? (
          <Sheet open={modalOpen} onOpenChange={handleCloseModal}>
            <SheetContent side="bottom" className="max-h-[90vh] rounded-t-3xl px-0 pb-[calc(1rem+env(safe-area-inset-bottom))]">
              <SheetHeader className="px-4 pb-0 text-left">
                <SheetTitle>{featuredModal.title}</SheetTitle>
                <SheetDescription>{featuredModal.description}</SheetDescription>
              </SheetHeader>
              <div className="px-4 py-4">{modalBody}</div>
              <SheetFooter className="px-4 pt-0">
                <Button onClick={() => handleCloseModal(false)} className="h-11 w-full gap-2 text-sm font-semibold">
                  <Award className="h-4 w-4" />
                  {t('training.results.continue')}
                </Button>
              </SheetFooter>
            </SheetContent>
          </Sheet>
        ) : (
          <Dialog open={modalOpen} onOpenChange={handleCloseModal}>
            <DialogContent className="max-w-2xl rounded-3xl border-none p-0 shadow-2xl">
              <div className="p-6 sm:p-7">
                <DialogHeader className="mb-4 text-left">
                  <DialogTitle className="text-2xl tracking-tight">{featuredModal.title}</DialogTitle>
                  <DialogDescription>{featuredModal.description}</DialogDescription>
                </DialogHeader>
                {modalBody}
                <DialogFooter className="mt-6">
                  <Button onClick={() => handleCloseModal(false)} className="h-11 gap-2 px-5 text-sm font-semibold">
                    <Award className="h-4 w-4" />
                    {t('training.results.continue')}
                  </Button>
                </DialogFooter>
              </div>
            </DialogContent>
          </Dialog>
        )
      ) : null}

      <AnimatePresence>
        {visibleCards.length > 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 16 }}
            className="pointer-events-none fixed inset-x-4 bottom-4 z-50 flex flex-col gap-3 sm:inset-x-auto sm:right-4 sm:w-[26rem]"
          >
            {visibleCards.map((item) => (
              <motion.div
                key={item.unlockKey}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 12 }}
                className="pointer-events-auto relative"
              >
                <button
                  type="button"
                  onClick={() => {
                    setVisibleCards([]);
                    onHandled();
                  }}
                  className="absolute right-3 top-3 z-10 rounded-full bg-background/80 p-1 text-muted-foreground transition-colors hover:text-foreground"
                  aria-label={currentLang === 'uk' ? 'Закрити' : 'Dismiss'}
                >
                  <X className="h-4 w-4" />
                </button>
                <AchievementPanel achievement={item} compact />
              </motion.div>
            ))}
          </motion.div>
        ) : null}
      </AnimatePresence>
    </>
  );
}
